import React, { useState } from 'react'
import { Stack, Typography, Button, Grid, Tooltip, IconButton } from '@mui/material'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import { useDispatch } from 'react-redux'
import { setCreateTeamModalOpen } from '@/Redux/appStateSlice'

interface PlanComparisonTableProps {
  onGetStarted: (planKey: string) => void
  onChatToSales: (planKey: string) => void
  showActions?: boolean
  isCollapsible?: boolean
  showFeatureDescriptions?: boolean
}

const plans = [
  { key: 'free', label: 'Free' },
  { key: 'team', label: 'Team' },
  { key: 'org', label: 'Organization' },
]

const sections = [
  {
    title: 'Activities',
    color: '#A12F7E',
    features: [
      {
        label: 'Page (Question) Types',
        values: ['All 6', 'All 6', 'All 6'],
        description: 'Number of different question/page types you can use in activities.',
      },
      {
        label: 'Add Media to Pages',
        values: ['check', 'check', 'check'],
        description: 'Ability to add images, audio, or video to activity pages.',
      },
      {
        label: 'AI Activity Builder',
        values: ['check', 'check', 'check'],
        description: 'Use AI to help create activities automatically.',
      },
      {
        label: 'Bulk Page Creator',
        values: ['check', 'check', 'check'],
        description: 'Create multiple pages at once for faster activity building.',
      },
      {
        label: 'Assign as Practice',
        values: ['check', 'check', 'check'],
        description: 'Assign activities as practice for students.',
      },
      {
        label: 'Assign as Assessment',
        values: ['dash', 'dash', 'dash'],
        description: 'Assign activities as formal assessments.',
      },
    ],
  },
  {
    title: 'AI Grading',
    color: '#A12F7E',
    features: [
      { label: 'Rubric', values: ['check', 'check', 'check'], description: 'AI grades using a detailed rubric.' },
      {
        label: 'Proficiency',
        values: ['check', 'check', 'check'],
        description: 'AI estimates proficiency level based on responses.',
      },
      {
        label: 'Pass-Fail',
        values: ['check', 'check', 'check'],
        description: 'AI determines if the response meets a set bar.',
      },
      {
        label: 'Correct Answer',
        values: ['check', 'check', 'check'],
        description: 'AI checks if the answer matches the correct answer.',
      },
    ],
  },
  {
    title: 'AI Feedback',
    color: '#A12F7E',
    features: [
      { label: 'Grading Results', values: ['check', 'check', 'check'], description: 'See the results of AI grading.' },
      { label: 'Grammar Insights', values: ['check', 'check', 'check'], description: 'AI provides grammar feedback.' },
      {
        label: 'Suggested Response',
        values: ['check', 'check', 'check'],
        description: 'AI suggests improved responses.',
      },
      {
        label: 'Proficiency Level Estimate',
        values: ['check', 'check', 'check'],
        description: 'AI estimates the proficiency level for the response.',
      },
    ],
  },
  {
    title: 'Progress Tracking',
    color: '#A12F7E',
    features: [
      {
        label: 'Student Portfolios',
        values: ['dash', 'check', 'check'],
        description: 'Track and view student work over time.',
      },
      {
        label: 'Custom Standards',
        values: ['dash', 'dash', 'dash'],
        description: 'Set and track custom standards for your organization.',
      },
      {
        label: 'Dashboard & Reports',
        values: ['dash', 'dash', 'check'],
        description: 'Access dashboards and reports for progress tracking.',
      },
    ],
  },
  {
    title: 'Collaboration',
    color: '#A12F7E',
    features: [
      {
        label: 'Discover Library',
        values: ['check', 'check', 'check'],
        description: 'Access a library of shared activities.',
      },
      {
        label: 'Team Workspace',
        values: ['1 Folder', 'check', 'check'],
        description: 'Collaborate with your team in a shared workspace.',
      },
      {
        label: 'District Library',
        values: ['dash', 'dash', 'check'],
        description: 'Access a library shared across your district.',
      },
      {
        label: 'Organization Management',
        values: ['dash', 'dash', 'check'],
        description: 'Manage users and settings for your organization.',
      },
    ],
  },
  {
    title: 'Integrations',
    color: '#A12F7E',
    features: [
      {
        label: 'Clever',
        values: ['check', 'check', 'check'],
        description: 'Integration with Clever for rostering and SSO.',
      },
      {
        label: 'Google',
        values: ['SSO & Rosters', 'check', 'check'],
        description: 'Integration with Google for SSO and roster sync.',
      },
      { label: 'Canvas', values: ['dash', 'dash', 'check'], description: 'Integration with Canvas LMS.' },
      { label: 'Schoology', values: ['dash', 'dash', 'check'], description: 'Integration with Schoology LMS.' },
      { label: 'Moodle', values: ['dash', 'dash', 'check'], description: 'Integration with Moodle LMS.' },
      { label: 'Brightspace', values: ['dash', 'dash', 'check'], description: 'Integration with Brightspace LMS.' },
      { label: 'Blackboard', values: ['dash', 'dash', 'check'], description: 'Integration with Blackboard LMS.' },
    ],
  },
  {
    title: 'Support',
    color: '#A12F7E',
    features: [
      { label: 'Chat & Email', values: ['dash', 'check', 'check'], description: 'Support via chat and email.' },
      {
        label: 'Onboarding & Setup',
        values: ['dash', 'check', 'check'],
        description: 'Help with onboarding and setup.',
      },
      {
        label: 'Professional Development',
        values: ['dash', '1 per year', 'check'],
        description: 'Access to professional development sessions.',
      },
      {
        label: 'Dedicated Success Manager',
        values: ['dash', 'dash', 'check'],
        description: 'A dedicated manager for your organization.',
      },
    ],
  },
]

function renderCell(value: string) {
  if (value === 'check') {
    return <CheckCircleOutlineRoundedIcon sx={{ color: '#00B67A' }} />
  }
  if (value === 'dash') {
    return <Typography color="#B0B0B0">—</Typography>
  }

  return (
    <Typography color="#475467" fontWeight={400}>
      {value}
    </Typography>
  )
}

export default function PlanComparisonTable({
  onGetStarted,
  onChatToSales,
  showActions = true,
  isCollapsible = false,
  showFeatureDescriptions = true,
}: PlanComparisonTableProps) {
  const [isExpanded, setIsExpanded] = useState(!isCollapsible)
  const dispatch = useDispatch()
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Grid item xs={12}>
      <Stack gap="12px" justifyContent="center" sx={{ width: '100%', overflowX: 'auto', p: { xs: 1, md: 4 } }}>
        {isCollapsible && !isExpanded ? (
          <Button endIcon={<ArrowDownwardRoundedIcon />} variant="text" onClick={toggleExpanded}>
            Compare all features
          </Button>
        ) : (
          <>
            {isCollapsible ? (
              <Button endIcon={<ArrowUpwardRoundedIcon />} variant="text" onClick={toggleExpanded}>
                Compare all features
              </Button>
            ) : null}
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 320, borderBottom: '1px solid #EAECF0' }} />
                  {plans.map(plan => (
                    <th
                      key={plan.key}
                      style={{
                        textAlign: 'center',
                        padding: '16px 0',
                        fontSize: 20,
                        fontWeight: 600,
                        color: '#101828',
                        borderBottom: '1px solid #EAECF0',
                      }}
                    >
                      {plan.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sections.map((section, i) => (
                  <React.Fragment key={section.title + String(i)}>
                    <tr>
                      <td colSpan={4} style={{ padding: '24px 0 8px 0', alignItems: 'center' }}>
                        <Typography color={section.color} fontSize={14} fontWeight={600} p="0px 24px 16px 24px">
                          {section.title}
                        </Typography>
                      </td>
                    </tr>
                    {section.features.map((feature, j) => (
                      <tr key={feature.label} style={{ background: j % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                        <td
                          style={{
                            fontWeight: 500,
                            color: '#101828',
                            fontSize: 14,
                            display: 'flex',
                            height: '64px',
                            lineHeight: '20px',
                            padding: '0px 24px',
                            gap: '12px',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {feature.label}
                            {showFeatureDescriptions && feature.description ? (
                              <Tooltip
                                arrow
                                componentsProps={{
                                  arrow: { sx: { color: '#000 !important' } },
                                  tooltip: {
                                    sx: {
                                      bgcolor: '#000 !important',
                                      p: '8px',
                                      border: `1px solid '#000 !important'`,
                                      color: '#fff !important',
                                      borderRadius: '8px',
                                      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                                    },
                                  },
                                }}
                                placement="right"
                                title={feature.description}
                              >
                                <IconButton size="small" sx={{ p: 0 }}>
                                  <HelpOutlineRoundedIcon
                                    fontSize="small"
                                    sx={{ height: 16, width: 16, color: '#B0B0B0 !important' }}
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </span>
                        </td>
                        {feature.values.map((value, idx) => (
                          <td key={idx} style={{ textAlign: 'center', padding: '12px 0' }}>
                            {renderCell(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {showActions ? (
              <Stack direction="row" gap={2} justifyContent="flex-end" mt={4}>
                {plans.map(plan => (
                  <Stack key={plan.key} alignItems="center" gap={1} minWidth={250}>
                    <Button
                      fullWidth
                      sx={{
                        bgcolor: '#EB3EB7',
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#d11fa0' },
                      }}
                      variant="contained"
                      onClick={() => {
                        onGetStarted(plan.key)
                        dispatch(setCreateTeamModalOpen(true))
                      }}
                    >
                      Get started
                    </Button>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={() => {
                        onChatToSales(plan.key)
                      }}
                    >
                      Chat to sales
                    </Button>
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
    </Grid>
  )
}
